import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import ReactInputMask from "react-input-mask";

// Api
import { getEventById } from "../../api/plan";
import { createTicket } from "../../api/subscription";

// Components
import Layout from "../../components/Layout";
import TicketInfoModal from "./TicketInfoModal";
import TermsAndConditionsModal from "./TermsConditionsModal";
import PaymentSuccessModal from "./PaymentSuccessModal";
import PaymentErrorModal from "./PaymentErrorModal";

// Images
import img1 from '../../assets/images/6843fecabc4c4263bf677a06.jpg';
import img2 from '../../assets/images/6810d0862c4130f769b80d6e.jpg';
import img3 from '../../assets/images/68666ca1ff1f250645e93163.jpg';

export const imageMap = {
  '6843fecabc4c4263bf677a06': img1,
  '6810d0862c4130f769b80d6e': img2,
  '68666ca1ff1f250645e93163': img3
};

const PlanDetailsPage = () => {
	const {id} = useParams();
	const [searchParams] = useSearchParams();
	const payment = searchParams.get('payment');
	const navigate = useNavigate();
	const [isPageLoading, setIsPageLoading] = useState(true);
	const [eventData, setEventData] = useState({});
	const [responseData, setResponseData] = useState(false);
	const [isTicketInfoModalOpen, setIsTicketInfoModalOpen] = useState(false);
	const [isTermsConditionsModalOpen, setIsTermsConditionsModalOpen] = useState(false);
	const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] = useState(payment === 'success');
	const [isPaymentErrorModalOpen, setIsPaymentErrorModalOpen] = useState(payment === 'error');
	const { register, handleSubmit, control, setValue, formState: { isValid, isSubmitting, errors }, watch } = useForm();
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
	const isAndroid = /Android/i.test(navigator.userAgent);
	
	const getEventData = async (id) => {
		setIsPageLoading(true);
		const response = await getEventById(id); // get plan data instead
		setEventData(response?.data);
		setIsPageLoading(false);
	};
	
	const onSubmit = async (data) => {
		const response = await createTicket({
			eventId: id,
			ticket: data?.ticket,
			name: data?.name,
			email: data?.email,
			phone: data?.phone,
			paymentMethod: data?.paymentMethod,
		});
		setResponseData(response?.data);
		if (response?.data?.data?.status === 'success') {
			if (response?.data?.data?.redirect_url) {
				window.location.href = response?.data?.data?.redirect_url;
			}
			if (response?.data?.data?.widget_url) {
				navigate(`/payment/apple/?widgetUrl=${response?.data?.data?.widget_url}&eventId=${id}`);
			}
		}
		setIsPageLoading(false);
	};

	const priceMapping = eventData?.data?.ticketTypes?.reduce((acc, item) => {
		acc[item._id] = item;
		return acc;
	}, {});
	const selectedTicket = watch("ticket");
	
	useEffect(() => {
		getEventData(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const handleCloseTermsAndConditionsModal = (value) => {
        if (value) setValue('isAgreed', true, { shouldValidate: true });
        setIsTermsConditionsModalOpen(false);
    }

	const handleClosePaymentSuccessModal = () => {
        setIsPaymentSuccessModalOpen(false);
    }

	const handleClosePaymentErrorModal = () => {
        setIsPaymentErrorModalOpen(false);
    }
	
	return (
		<Layout>
			{isTermsConditionsModalOpen && <TermsAndConditionsModal onClose={handleCloseTermsAndConditionsModal} />}
			{isPaymentSuccessModalOpen && <PaymentSuccessModal phone={eventData?.data?.supportPhoneNumber} onClose={handleClosePaymentSuccessModal} />}
			{isPaymentErrorModalOpen && <PaymentErrorModal phone={eventData?.data?.supportPhoneNumber} onClose={handleClosePaymentErrorModal} />}
			<TicketInfoModal eventData={eventData} isOpen={isTicketInfoModalOpen} onClose={() => setIsTicketInfoModalOpen(false)}/>
			<div className="min-h-screen bg-[#f5f3ee] flex items-center flex-col justify-center p-0 lg:p-2">
				<div className="flex flex-col lg:flex-row w-full max-w-6xl bg-white shadow-2xl rounded-2xl overflow-hidden mt-3 lg:mt-0">
				
				{/* Sol tərəf - Event Promo */}
				<div	
					className="lg:w-1/2 w-full bg-black text-white flex flex-col justify-between bg-cover"
					style={{ backgroundImage: `url(${imageMap[eventData?.data?._id]})`, backgroundPosition: 'center'}}
				>
					<div className="flex flex-col items-center lg:items-start text-center lg:text-left bg-black bg-opacity-70 p-8 lg:p-10 h-full">
					<h2 className="text-2xl lg:text-3xl font-extrabold mb-4 leading-tight">
					{eventData?.data?.title}
					</h2>
					<div dangerouslySetInnerHTML={{__html: eventData?.data?.description}}></div>
					</div>
				</div>
				
				{/* Sağ tərəf - Form */}
				<div className="lg:w-1/2 w-full bg-[#f5f3ee] p-8 lg:p-10">
				<h2 className="text-xl lg:text-2xl font-extrabold text-gray-900 mb-2">XOŞ GƏLMİSİNİZ!</h2>
				<p className="text-gray-600 mb-8 text-sm lg:text-base">
				Bilet almaq üçün formu doldurun
				</p>
				<form className="space-y-5">
				<div>
				<label className="text-sm font-medium text-gray-700 mb-1 flex items-end">Bilet Növü* 
					<button type="button" className="text-blue-700 ml-2" variant="ghost" size="icon" onClick={() => setIsTicketInfoModalOpen(true)}>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6"><path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" /></svg>
					</button>
				</label>
				<select
					id="ticket"
					{...register("ticket", { required: true })}
					className="w-full border border-gray-300 rounded-lg p-3 bg-white focus:outline-none focus:ring-2 focus:ring-black"
				>
					<option selected disabled value="">Bilet növü seçin</option>
					{eventData?.data?.ticketTypes?.map(({_id, name}) => <option value={_id}>{name?.az}</option>)}
				</select>
				</div>
				<div>
				<label className="block text-sm font-medium text-gray-700 mb-1">Ad, Soyad*</label>
				<Controller
				control={control}
				name="name"
				render={() => (
					<input
					{...register("name", {
						required: true,
					})}
					id="name"
					placeholder="Ad, Soyad"
					className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
					required
					/>
				)}
				/>
				</div>
				<div>
				<label className="text-sm font-medium text-gray-700 mb-1 flex items-end align-center">Email* 
					&nbsp;<i>(Biletiniz bu ünvana göndəriləcək.)</i>
				</label>
				<Controller
				control={control}
				name="email"
				render={() => (
					<input
					{...register("email", {
						required: true,
						pattern: {
							value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
							message: "invalid email address"
						}
					})}
					type="email"
					id="email"
					placeholder="Email"
					className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
					required
					/>
				)}
				/>
				</div>
				<div>
				<label className="block text-sm font-medium text-gray-700 mb-1">Telefon*</label>
				<Controller
				control={control}
				name="phone"
				render={() => (
					<ReactInputMask
					mask="099-999-9999"
					id="phone"
					{...register("phone", {
						required: true,
						pattern: /0\d\d-\d\d\d-\d\d\d\d/
					 })}
					placeholder="(0XX) XXX-XX-XX"
					className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
					required
					/>
				)}
				/>
				</div>
				<label className="block text-sm font-medium text-gray-700">Ödəmə metodu*</label>
				<div>
					<Controller
						control={control}
						name="paymentMethod"
						defaultValue="card"
						rules={{ required: true }}
						render={({ field }) => (
						<div className="flex flex-col gap-3 first:-mt-3">
							{eventData?.data?.paymentModes?.map((mode) => {
								if (mode?.type === 'apple-pay' && !isIOS) return null;
								if (mode?.type === 'google-pay' && !isAndroid) return null;
								return (
									<>
										<label
										className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
											field.value === mode?.type ? 'border-black bg-gray-50' : 'border-gray-300'
										}`}
										>
										<input
											type="radio"
											value={mode?.type}
											checked={field.value === mode?.type}
											onChange={field.onChange}
											className="form-radio h-4 w-4 text-black"
										/>
										<div dangerouslySetInnerHTML={{__html: mode?.icon}} className="h-6 w-6 text-gray-700" />
										<span>{mode?.name?.az}</span>
									</label>
									</>
								);
							})}
						</div>
						)}
					/>
				</div>

				{errors?.root?.serverError && <div className="text-red-600 mb-2">Server xətası.</div>}
				<div className="w-full items-center pt-2 pb-1">
                    <div className="flex items-center">
                        <input {...register("isAgreed", {required: true})} id="checked-checkbox-agreement" type="checkbox" className="w-4 h-4 text-blue-700 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2" />
                        <label className="ms-2 text-sm font-medium text-gray-800"><button type="button" className="text-blue-700 underline font-bold" onClick={() => setIsTermsConditionsModalOpen(true)}>Şərtlər</button>-i oxudum və qəbul edirəm.</label>
                    </div>
                </div>
				<button
					disabled={!isValid || isSubmitting}
					className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-all font-bold disabled:bg-gray-600 flex items-center justify-center space-x-2 text-lg"
					onClick={handleSubmit(onSubmit)}
					>
					{selectedTicket ? (
						priceMapping[selectedTicket]?.salePrice ? (
						<>
							<span className="text-red-400 line-through text-sm">
							{priceMapping[selectedTicket]?.salePrice}₼
							</span>
							<span className="text-green-400 font-semibold text-lg">
							{priceMapping[selectedTicket]?.price}₼
							</span>
							<span>{isSubmitting ? 'Gözləyin...' : 'Ödə'}</span>
						</>
						) : (
						<>
							<span className="text-white font-semibold">
							{priceMapping[selectedTicket]?.price}₼
							</span>
							<span>{isSubmitting ? 'Gözləyin...' : 'Ödə'}</span>
						</>
						)
					) : (
						'0₼ Ödə'
					)}
				</button>
				</form>
				</div>
				</div>
			</div>
		</Layout>
	);
};

export default PlanDetailsPage;
